import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import map from 'lodash/map';
import { IconCaretRight } from 'svgIcons/MotionPortalIcons';
import { tabIDs } from '../../../components/PowertrainAssetDetails/DetailedInformation/constants';
import { multiChartOptions, opButtons } from '../../../components/PowertrainAssetDetails/DetailedInformation/OperationalParametersV2/constants';
import { TrendComments } from '../../../components/PowertrainAssetDetails/DetailedInformation/OperationalParametersV2/SingleChartView/Trend/TrendComments';
import useUserCapability from 'sharedHooks/useUserCapability';

/** General Tabs component
 *
 * @param {variant} - Allowed values: "primary", "secondary", "tertiary", "panel"
 * @param {children} - Renderable children. Normally either result from Router switch or selected component from options object
 * @param {options} - Object with tab options
 * @param {handleTabSelection}
 * @param {activatedTabKey} - Object key from options
 */

export const GeneralTabContent = ({ variant, children, options, handleTabSelection, activatedTabKey, activeDetailedInformationTab, showScrollbar, parentElementId, tabContentClass, conditionBasedClassName = '', activeOPTab, multiChart = 'single' }) => {
    let lastScrollLeft = 0;
    const { t: translate } = useTranslation();
    const horizontalTabRef = useRef(null);
    const horizontalTabContinerRef = useRef(null);
    const tabRef = useRef({});
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const userCapabilityHelper = useUserCapability();

    /* FOR PARENT/PANEL RESIZE */
    const onParentResize = (element, callback) => {
        const resizeObserver = new ResizeObserver(() => callback(element));
        resizeObserver.observe(element);
    };

    const tabScrollIntoView = (tabKey) => {
        if (tabRef.current) {
            tabRef.current[tabKey || activatedTabKey]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    };

    const handleResize = () => {
        if (horizontalTabRef.current && horizontalTabContinerRef.current) {
            const containerWidth = horizontalTabContinerRef.current.scrollWidth;
            const clientWidth = horizontalTabRef.current.scrollWidth;
            setShowRightArrow(containerWidth < clientWidth);
        }
        tabScrollIntoView();
    };

    const handleParentResize = (parentElement) => {
        if (parentElement && horizontalTabRef.current) {
            const containerWidth = parentElement.scrollWidth;
            const clientWidth = horizontalTabRef.current.scrollWidth;
            setShowRightArrow(containerWidth < clientWidth);
        }
        tabScrollIntoView();
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize, false);
        const parentElement = parentElementId && document.getElementById(parentElementId);
        if (parentElement) {
            onParentResize(parentElement, handleParentResize);
        }
        return () => window.removeEventListener('resize', handleResize);
    }, [horizontalTabRef.current, parentElementId]);

    useEffect(() => {
        tabScrollIntoView();
    }, [tabRef]);

    const handleTabClick = (e) => {
        if (handleTabSelection) {
            handleTabSelection(e);
        }
        tabScrollIntoView(e.target.id);
    };

    const handleScroll = (ref, type) => {
        if (ref) {
            if (type === 'left') {
                ref.scrollLeft -= 200; // Scroll left
            } else if (type === 'right') {
                ref.scrollLeft += 200; // Scroll right
            }
            const currentScrollLeft = horizontalTabRef.current.scrollLeft;
            if (currentScrollLeft > lastScrollLeft) {
                setShowLeftArrow(true);
            }
            if (ref.scrollLeft === 0) {
                setShowLeftArrow(false);
            }
            if (ref.scrollLeft + ref.clientWidth >= ref.scrollWidth) {
                setShowRightArrow(false);
            } else {
                setShowRightArrow(true);
            }
            lastScrollLeft = currentScrollLeft;
        }
    };

    const tabOption = (option, optionKey) => {
        const isDetailedInformationTab = activeDetailedInformationTab && option.tabID === translate('ABB.Powertrain.Frontend.ptAssetDetailsDetailedInformationTabID');
        return (
            <div
                ref={(el) => { if (tabRef.current) { tabRef.current[optionKey] = el; } }}
                className={`tab ${optionKey === activatedTabKey || isDetailedInformationTab ? 'selected' : ''} ${option.disabled ? 'disabled' : ''}`}
                id={optionKey}
                onClick={handleTabClick}
                aria-disabled={option.disabled}
                tabIndex={option.disabled ? -1 : 0}
                onKeyDown={(e) => { if (option.disabled) { return; } if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTabClick(e); } }}
                key={optionKey}
                role='tab'
            >
                {option?.titleIcon && <span className='tab-icon'>{option.titleIcon}</span>}
                {option.title}
            </div>
        );
    };

    const isOPTrendTab = userCapabilityHelper.hasOperationalParametersComments() && activatedTabKey === tabIDs.operationalParameters &&
        activeOPTab === opButtons.trends && multiChart !== multiChartOptions.MULTI;

    return (
        <div className='scrollable-tab-container'>
            <div className={`scrollable-tab-header ${conditionBasedClassName}`} ref={horizontalTabContinerRef}>
                {showLeftArrow && <button className='scroll-btn left-btn' onClick={() => handleScroll(horizontalTabRef.current, 'left')}><IconCaretRight /></button>}
                <div
                    ref={horizontalTabRef}
                    onScroll={() => handleScroll(horizontalTabRef.current, null)}
                    className={`tab-line + ${variant} ${!showScrollbar ? 'scroll-hide' : ''}`}>
                    {map(options, (option, optionKey) => option.hidden ? null : tabOption(option, optionKey))}
                </div>
                {showRightArrow && <button className='scroll-btn right-btn' onClick={() => handleScroll(horizontalTabRef.current, 'right')}><IconCaretRight /></button>}
            </div>
            <div className={`scrollable-tab-content-container ${isOPTrendTab ? 'with-comment-sidebar' : ''}`}>
                <div className={`scrollable-tab-content ${tabContentClass || ''} ${variant === 'tertiary' ? 'tab-offset' : ''}`}>
                    {children}
                </div>
                {isOPTrendTab && <TrendComments />}
            </div>
        </div>
    );
};

GeneralTabContent.propTypes = {
    variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'panel']),
    children: PropTypes.node,
    handleTabSelection: PropTypes.func,
    options: PropTypes.objectOf(
        PropTypes.shape({
            title: PropTypes.string,
            titleIcon: PropTypes.node,
            disabled: PropTypes.bool,
            hidden: PropTypes.bool,
            component: PropTypes.node,
            tabID: PropTypes.string,
        })
    ),
    activatedTabKey: PropTypes.string,
    activeDetailedInformationTab: PropTypes.bool,
    showScrollbar: PropTypes.bool,
    parentElementId: PropTypes.string,
    tabContentClass: PropTypes.string,
    conditionBasedClassName: PropTypes.string,
    activeOPTab: PropTypes.string,
    multiChart: PropTypes.string,
};
